import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  ExternalLink, 
  MessageCircle, 
  Home as HomeIcon,
  Users,
  Package,
  Briefcase,
  Mail,
  Globe,
  Phone,
  Copy,
  CheckCheck
} from 'lucide-react';

const InternalTesting = () => {
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);

  const [testResults, setTestResults] = useState({});
  const [copiedLink, setCopiedLink] = useState('');

  const whatsappNumber = '+968 9937 8612';
  const whatsappFormatted = '96899378612';
  
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
  
  const testEmailConfiguration = async () => {
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/test-email`);
      const data = await response.json();
      setEmailTestResult(data);
    } catch (error) {
      setEmailTestResult({
        success: false,
        message: `Connection error: ${error.message}`
      });
    } finally {
      setEmailTesting(false);
    }
  };

    setCopiedLink(id);
    setTimeout(() => setCopiedLink(''), 2000);
  };

  const markTest = (testId, status) => {
    setTestResults(prev => ({ ...prev, [testId]: status }));
  };

  const TestSection = ({ title, children, id }) => (
    <Card className="mb-6 border-l-4 border-[#1a3a52]">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-[#1a3a52]">{title}</h3>
          {testResults[id] && (
            <span className={`flex items-center space-x-1 ${
              testResults[id] === 'pass' ? 'text-green-600' : 'text-red-600'
            }`}>
              {testResults[id] === 'pass' ? (
                <>
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm font-semibold">Passed</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5" />
                  <span className="text-sm font-semibold">Failed</span>
                </>
              )}
            </span>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1a3a52] to-[#2a4a62] text-white rounded-lg p-8 mb-8 shadow-xl">
          <h1 className="text-4xl font-bold mb-3">
            🔬 Taj Gulf Trading LLC - Internal Testing Suite
          </h1>
          <p className="text-xl opacity-90">
            Complete Website Functionality Testing
          </p>
          <p className="text-sm opacity-75 mt-2">
            Test all features before deployment • Contact: {whatsappNumber}
          </p>
        </div>

        {/* Critical Notice */}
        <Card className="mb-8 border-l-4 border-red-500 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-red-900 mb-2">
                  About WhatsApp Connection Issues
                </h3>
                <div className="text-red-800 text-sm space-y-2">
                  <p>
                    <strong>If WhatsApp links don't work from your location/network:</strong>
                  </p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Your corporate network/firewall may be blocking WhatsApp domains</li>
                    <li>Some countries/networks restrict WhatsApp Web access</li>
                    <li>Browser security settings may prevent opening wa.me links</li>
                    <li>WhatsApp app may not be installed on your device</li>
                  </ul>
                  <p className="font-semibold mt-3">
                    ✓ This is NOT a website bug - the links are correctly coded and will work for customers
                  </p>
                  <p className="mt-2">
                    <strong>Alternative:</strong> Try testing from a mobile phone with WhatsApp installed, 
                    or from a different network (like mobile data instead of office WiFi).
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test 1: Page Navigation */}
        <TestSection title="1. Page Navigation & Accessibility" id="navigation">
          <div className="space-y-3">
            <p className="text-gray-700 mb-4">
              Test that all pages load correctly and navigation works:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Link to="/" target="_blank">
                <Button variant="outline" className="w-full justify-start">
                  <HomeIcon className="h-4 w-4 mr-2" />
                  Home Page
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              </Link>
              <Link to="/about" target="_blank">
                <Button variant="outline" className="w-full justify-start">
                  <Users className="h-4 w-4 mr-2" />
                  About Us
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              </Link>
              <Link to="/products" target="_blank">
                <Button variant="outline" className="w-full justify-start">
                  <Package className="h-4 w-4 mr-2" />
                  Products
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              </Link>
              <Link to="/services" target="_blank">
                <Button variant="outline" className="w-full justify-start">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Services
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              </Link>
              <Link to="/contact" target="_blank">
                <Button variant="outline" className="w-full justify-start">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Us
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              </Link>
            </div>
            <div className="mt-4 flex space-x-2">
              <Button onClick={() => markTest('navigation', 'pass')} size="sm" className="bg-green-600">
                <CheckCircle2 className="h-4 w-4 mr-1" /> All Pages Load
              </Button>
              <Button onClick={() => markTest('navigation', 'fail')} size="sm" variant="destructive">
                <XCircle className="h-4 w-4 mr-1" /> Issues Found
              </Button>
            </div>
          </div>
        </TestSection>

        {/* Test 2: Language Switcher */}
        <TestSection title="2. Bilingual Functionality (English/Arabic)" id="language">
          <div className="space-y-3">
            <p className="text-gray-700 mb-3">
              Test language switching between English and Arabic:
            </p>
            <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
              <li>Look for "AR" button in top-right corner of any page</li>
              <li>Click it - page should switch to Arabic (RTL layout)</li>
              <li>Click again - should switch back to English</li>
              <li>All content (menus, buttons, text) should translate</li>
            </ol>
            <div className="bg-blue-50 p-4 rounded-lg mt-3">
              <p className="text-sm text-blue-900">
                <strong>Expected Result:</strong> Language toggle button visible, content translates smoothly, 
                Arabic shows right-to-left layout.
              </p>
            </div>
            <div className="mt-4 flex space-x-2">
              <Button onClick={() => markTest('language', 'pass')} size="sm" className="bg-green-600">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Works Perfectly
              </Button>
              <Button onClick={() => markTest('language', 'fail')} size="sm" variant="destructive">
                <XCircle className="h-4 w-4 mr-1" /> Issues Found
              </Button>
            </div>
          </div>
        </TestSection>

        {/* Test 3: Products Display */}
        <TestSection title="3. Products Display & Filtering" id="products">
          <div className="space-y-3">
            <p className="text-gray-700 mb-3">
              Test product catalog functionality:
            </p>
            <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
              <li>Go to Products page - should see 9 products displayed</li>
              <li>Test category filters (Breakfast, Pasta, Baking, Beverages, etc.)</li>
              <li>Check grid/list view toggle buttons work</li>
              <li>Verify all product images load correctly</li>
              <li>Check product names display in both English & Arabic</li>
            </ol>
            <div className="bg-gray-100 p-4 rounded-lg mt-3">
              <p className="text-sm font-semibold mb-2">Products to verify:</p>
              <ul className="text-xs space-y-1 text-gray-700">
                <li>✓ Bliss Corn Flakes</li>
                <li>✓ Tate & Lyle Dark Brown Sugar</li>
                <li>✓ Barilla Penne Rigate No.73</li>
                <li>✓ Rawan Whole Rolled Oats</li>
                <li>✓ Al Shafaa Natural Honey - 4kg</li>
                <li>✓ Al Shafaa Natural Honey - 500g</li>
                <li>✓ Heavens Refined Olive Oil - 5L</li>
                <li>✓ San Pellegrino Sparkling Water</li>
                <li>✓ MDH Deggi Mirch Chilli Powder</li>
              </ul>
            </div>
            <div className="mt-4 flex space-x-2">
              <Button onClick={() => markTest('products', 'pass')} size="sm" className="bg-green-600">
                <CheckCircle2 className="h-4 w-4 mr-1" /> All Products Display
              </Button>
              <Button onClick={() => markTest('products', 'fail')} size="sm" variant="destructive">
                <XCircle className="h-4 w-4 mr-1" /> Issues Found
              </Button>
            </div>
          </div>
        </TestSection>

        {/* Test 4: WhatsApp Integration - Detailed */}
        <TestSection title="4. WhatsApp Integration (Critical)" id="whatsapp">
          <div className="space-y-4">
            <p className="text-gray-700 mb-3">
              <strong>IMPORTANT:</strong> WhatsApp links may not work from corporate networks/restricted locations.
            </p>
            
            {/* Direct Links to Copy/Test */}
            <div className="bg-[#25D366] bg-opacity-10 p-4 rounded-lg space-y-3">
              <p className="font-semibold text-gray-800">Method 1: Copy & Paste Links</p>
              <p className="text-sm text-gray-700">
                Copy these links and paste directly into your browser address bar:
              </p>
              
              <div className="space-y-2">
                <div className="bg-white p-3 rounded border">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Basic WhatsApp Link:</p>
                  <div className="flex items-center space-x-2">
                    <code className="text-xs flex-1 text-[#1a3a52] break-all">
                      https://wa.me/{whatsappFormatted}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(`https://wa.me/${whatsappFormatted}`, 'wa1')}
                    >
                      {copiedLink === 'wa1' ? <CheckCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="bg-white p-3 rounded border">
                  <p className="text-xs font-semibold text-gray-600 mb-1">With Business Message:</p>
                  <div className="flex items-center space-x-2">
                    <code className="text-xs flex-1 text-[#1a3a52] break-all">
                      https://wa.me/{whatsappFormatted}?text=Hello%20Taj%20Gulf%20Trading%20LLC
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(`https://wa.me/${whatsappFormatted}?text=Hello%20Taj%20Gulf%20Trading%20LLC`, 'wa2')}
                    >
                      {copiedLink === 'wa2' ? <CheckCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual Test */}
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="font-semibold text-gray-800 mb-2">Method 2: Visual Button Test</p>
              <p className="text-sm text-gray-700 mb-3">
                Check if green WhatsApp button appears on website:
              </p>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                <li>Open any page of the website</li>
                <li>Look at bottom-right corner</li>
                <li>Green circular button should be visible</li>
                <li>Hover over it - should show "Taj Gulf Trading LLC"</li>
              </ol>
            </div>

            {/* Contact Number Verification */}
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <p className="font-semibold text-yellow-900 mb-2">
                <Phone className="inline h-4 w-4 mr-1" />
                WhatsApp Contact Number Verification:
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-yellow-800">Display Format:</span>
                  <code className="bg-white px-3 py-1 rounded font-mono">{whatsappNumber}</code>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-yellow-800">Link Format:</span>
                  <code className="bg-white px-3 py-1 rounded font-mono">{whatsappFormatted}</code>
                </div>
                <p className="text-yellow-800 mt-2">
                  ✓ Number is correctly formatted for WhatsApp API
                </p>
              </div>
            </div>

            {/* Alternative Testing */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-semibold text-blue-900 mb-2">If Links Don't Work - Alternative Test:</p>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-blue-800">
                <li>Open WhatsApp app on your phone</li>
                <li>Manually start a new chat</li>
                <li>Enter number: <strong>{whatsappNumber}</strong></li>
                <li>This confirms the number is valid and active</li>
                <li>The website will work the same way for customers</li>
              </ol>
            </div>

            <div className="mt-4 flex space-x-2">
              <Button onClick={() => markTest('whatsapp', 'pass')} size="sm" className="bg-green-600">
                <CheckCircle2 className="h-4 w-4 mr-1" /> WhatsApp Working
              </Button>
              <Button onClick={() => markTest('whatsapp', 'fail')} size="sm" variant="destructive">
                <XCircle className="h-4 w-4 mr-1" /> Cannot Test (Network Issue)
              </Button>
            </div>
          </div>
        </TestSection>

        {/* Test 5: Contact Form */}
        <TestSection title="5. Contact Form Functionality" id="contact">
          <div className="space-y-3">
            <p className="text-gray-700 mb-3">
              Test contact form on Contact Us page:

        {/* Test 4.5: Email Configuration */}
        <TestSection title="4.5 Email Configuration (Backend)" id="email-config">
          <div className="space-y-4">
            <p className="text-gray-700 mb-3">
              <strong>Test if email service is properly configured:</strong>
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-semibold text-blue-900 mb-2">How to Configure:</p>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-blue-800">
                <li>Open file: <code className="bg-white px-2 py-0.5 rounded">/app/backend/.env</code></li>
                <li>Find line: <code className="bg-white px-2 py-0.5 rounded">EMAIL_PASSWORD="YOUR_OUTLOOK_PASSWORD_HERE"</code></li>
                <li>Replace with your actual Outlook password</li>
                <li>Restart backend: <code className="bg-white px-2 py-0.5 rounded">sudo supervisorctl restart backend</code></li>
                <li>Click "Test Email Connection" button below</li>
              </ol>
            </div>

            <Button
              onClick={testEmailConfiguration}
              disabled={emailTesting}
              className="w-full md:w-auto bg-[#1a3a52] hover:bg-[#0f2535]"
            >
              {emailTesting ? 'Testing...' : 'Test Email Connection'}
            </Button>

            {emailTestResult && (
              <div className={`p-4 rounded-lg ${
                emailTestResult.success 
                  ? 'bg-green-50 border-l-4 border-green-500' 
                  : 'bg-red-50 border-l-4 border-red-500'
              }`}>
                <p className={`font-semibold ${
                  emailTestResult.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  {emailTestResult.success ? '✓ Success' : '✗ Failed'}
                </p>
                <p className={`text-sm mt-1 ${
                  emailTestResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {emailTestResult.message}
                </p>
                {!emailTestResult.success && (
                  <p className="text-xs text-red-700 mt-2">
                    See <strong>/app/HOW_TO_ADD_EMAIL_PASSWORD.md</strong> for detailed instructions
                  </p>
                )}
              </div>
            )}

            <div className="mt-4 flex space-x-2">
              <Button 
                onClick={() => markTest('email-config', emailTestResult?.success ? 'pass' : 'fail')} 
                size="sm" 
                className="bg-green-600"
                disabled={!emailTestResult}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Email Working
              </Button>
              <Button 
                onClick={() => markTest('email-config', 'fail')} 
                size="sm" 
                variant="destructive"
              >
                <XCircle className="h-4 w-4 mr-1" /> Not Configured Yet
              </Button>
            </div>
          </div>
        </TestSection>

            </p>
            <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
              <li>Go to Contact Us page</li>
              <li>Fill out all form fields (Name, Email, Phone, Business, Message)</li>
              <li>Click "Send Message" button</li>
              <li>Should see green success toast notification</li>
              <li>Form should clear after submission</li>
            </ol>
            <div className="bg-yellow-50 p-4 rounded-lg mt-3">
              <p className="text-sm text-yellow-900">
                <strong>Note:</strong> Contact form currently uses MOCK data (console.log). 
                Real email sending will be implemented in backend phase.
              </p>
            </div>
            <div className="mt-4 flex space-x-2">
              <Button onClick={() => markTest('contact', 'pass')} size="sm" className="bg-green-600">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Form Works
              </Button>
              <Button onClick={() => markTest('contact', 'fail')} size="sm" variant="destructive">
                <XCircle className="h-4 w-4 mr-1" /> Issues Found
              </Button>
            </div>
          </div>
        </TestSection>

        {/* Test 6: Responsive Design */}
        <TestSection title="6. Responsive Design (Mobile/Tablet)" id="responsive">
          <div className="space-y-3">
            <p className="text-gray-700 mb-3">
              Test website on different screen sizes:
            </p>
            <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
              <li>Open browser DevTools (F12)</li>
              <li>Click responsive design mode (phone icon)</li>
              <li>Test on: iPhone, iPad, Android sizes</li>
              <li>Check: Menu collapses to hamburger, content stacks properly</li>
              <li>Verify all buttons and links are clickable</li>
            </ol>
            <div className="mt-4 flex space-x-2">
              <Button onClick={() => markTest('responsive', 'pass')} size="sm" className="bg-green-600">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Responsive Works
              </Button>
              <Button onClick={() => markTest('responsive', 'fail')} size="sm" variant="destructive">
                <XCircle className="h-4 w-4 mr-1" /> Issues Found
              </Button>
            </div>
          </div>
        </TestSection>

        {/* Test 7: Logo and Branding */}
        <TestSection title="7. Logo & Brand Colors" id="branding">
          <div className="space-y-3">
            <p className="text-gray-700 mb-3">
              Verify brand elements display correctly:
            </p>
            <ul className="list-disc ml-5 space-y-2 text-sm text-gray-700">
              <li>Taj Gulf Trading LLC logo appears in header on all pages</li>
              <li>Logo is clear and not pixelated</li>
              <li>Brand colors (Navy Blue #1a3a52 and Gold #c5a365) are used throughout</li>
              <li>Footer displays company information correctly</li>
            </ul>
            <div className="flex items-center space-x-4 mt-4 p-4 bg-gray-100 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-12 h-12 rounded" style={{backgroundColor: '#1a3a52'}}></div>
                <span className="text-xs font-mono">#1a3a52</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-12 h-12 rounded" style={{backgroundColor: '#c5a365'}}></div>
                <span className="text-xs font-mono">#c5a365</span>
              </div>
            </div>
            <div className="mt-4 flex space-x-2">
              <Button onClick={() => markTest('branding', 'pass')} size="sm" className="bg-green-600">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Branding Correct
              </Button>
              <Button onClick={() => markTest('branding', 'fail')} size="sm" variant="destructive">
                <XCircle className="h-4 w-4 mr-1" /> Issues Found
              </Button>
            </div>
          </div>
        </TestSection>

        {/* Test Summary */}
        <Card className="bg-gradient-to-r from-[#1a3a52] to-[#2a4a62] text-white">
          <CardContent className="p-6">
            <h3 className="text-2xl font-bold mb-4">Testing Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {Object.entries({
                navigation: 'Navigation',
                language: 'Language',
                products: 'Products',
                whatsapp: 'WhatsApp',
                contact: 'Contact Form',
                responsive: 'Responsive',
                branding: 'Branding'
              }).map(([key, label]) => (
                <div key={key} className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-sm opacity-75 mb-1">{label}</p>
                  <p className="text-lg font-bold">
                    {testResults[key] === 'pass' ? '✅ Pass' : 
                     testResults[key] === 'fail' ? '❌ Fail' : '⏳ Pending'}
                  </p>
                </div>
              ))}
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="text-sm">
                <strong>Share this testing page:</strong>
              </p>
              <code className="text-xs mt-2 block break-all">
                {window.location.origin}/internal-testing
              </code>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mt-6">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-[#1a3a52] mb-4">Next Steps After Testing</h3>
            <ol className="list-decimal ml-5 space-y-2 text-gray-700">
              <li>Complete all tests above and mark results</li>
              <li>Report any issues found</li>
              <li>If satisfied with frontend, proceed with backend development</li>
              <li>Deploy to your Crazy Domain</li>
              <li>Test again on live domain</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InternalTesting;
